import { useState, useEffect, useRef, useCallback } from 'react'
import { useTokenAndOrgId, API_URL } from './utils'

const MAX_LOGS = 2000

export const useServerLogs = (
  instanceId: number,
  podName: string,
  containerName?: string
) => {
  const abortControllerRef = useRef<AbortController | null>(null)
  const [reconnectKey, setReconnectKey] = useState(0)

  const { token, orgId } = useTokenAndOrgId()
  const [logs, setLogs] = useState<string[]>([])
  const [isConnected, setIsConnected] = useState(false)

  const reconnect = useCallback(() => {
    setReconnectKey((prev) => prev + 1)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (!token || !orgId) {
      setLogs(['Authenticating...'])
      return
    }

    if (!instanceId || !podName) {
      setLogs(['Waiting for server details to fetch logs...'])
      return
    }

    console.log('Setting up fetch stream connection...', {
      token,
      orgId,
      instanceId,
      podName,
      containerName,
    })

    const startLogStream = async () => {
      try {
        // Create new abort controller for this request
        abortControllerRef.current = new AbortController()

        const podNameParam = encodeURIComponent(podName)
        const containerParam = containerName
          ? `&container=${encodeURIComponent(containerName)}`
          : ''
        const url = `${API_URL}/rpc/Inference/ServerPodLog?instance_id=${instanceId}&pod=${podNameParam}${containerParam}`

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Org-Id': orgId.toString(),
            Accept: 'text/plain',
          },
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        setIsConnected(true)
        setLogs(['--- Connection established ---'])

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('Response body is not readable')
        }

        const decoder = new TextDecoder()
        let buffer = ''

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            console.log('Stream ended - server closed connection')
            setIsConnected(false)
            setLogs((prev) =>
              [...prev, '--- Server closed connection ---'].slice(-MAX_LOGS)
            )
            break
          }

          // Check if we should abort
          if (abortControllerRef.current?.signal.aborted) {
            console.log('Stream aborted by client')
            break
          }

          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true })
          buffer += chunk

          // Process complete lines
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // Keep incomplete line in buffer

          if (lines.length > 0) {
            const newLogs = lines.filter((line) => line.trim() !== '')
            if (newLogs.length > 0) {
              setLogs((prev) => [...prev, ...newLogs].slice(-MAX_LOGS))
            }
          }
        }

        // Process any remaining buffer content when stream ends
        if (buffer.trim()) {
          setLogs((prev) => [...prev, buffer.trim()].slice(-MAX_LOGS))
        }
      } catch (error) {
        setIsConnected(false)

        if (error instanceof Error && error.name === 'AbortError') {
          // Don't log abort errors as they're expected when component unmounts
          console.log('Stream connection aborted')
          setLogs((prev) =>
            [...prev, '--- Connection closed ---'].slice(-MAX_LOGS)
          )
        } else {
          console.error('Fetch stream error:', error)
          setLogs((prev) =>
            [
              ...prev,
              `--- Error: ${error instanceof Error ? error.message : 'Unknown error'} ---`,
            ].slice(-MAX_LOGS)
          )
        }
      }
    }

    // Cancel any previous request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    startLogStream()

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      setIsConnected(false)
    }
  }, [instanceId, podName, containerName, token, orgId, reconnectKey])

  return { logs, isConnected, reconnect }
}
